import { useState, useCallback, useEffect } from 'react';
import { TWEAK_DEFAULTS } from '../theme';

/* ============================================================
   useTweaks — single source of truth for the live Tweaks panel.
   Holds the 11-dimension tweak config, persisted to localStorage
   so a reload keeps the user's skin. Folds in the panel's earlier
   standalone keys (instructorTheme / instructorSidebarCollapsed)
   for back-compat so existing users don't lose their mode/collapse.
   ============================================================ */

const STORAGE_KEY = 'instructorTweaks';

function readInitial() {
  let stored = {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) || {};
  } catch (_) { /* ignore corrupt JSON */ }

  // Migrate legacy single-purpose keys if the new blob is missing them.
  const legacy = {};
  if (stored.theme == null) {
    const m = localStorage.getItem('instructorTheme');
    if (m === 'dark' || m === 'light') legacy.theme = m;
  }
  if (stored.sidebar == null) {
    const c = localStorage.getItem('instructorSidebarCollapsed');
    if (c === '1') legacy.sidebar = 'icon';
    else if (c === '0') legacy.sidebar = 'labeled';
  }

  return { ...TWEAK_DEFAULTS, ...legacy, ...stored };
}

export default function useTweaks() {
  const [tweaks, setTweaks] = useState(readInitial);

  const setTweak = useCallback((keyOrEdits, val) => {
    const edits = (typeof keyOrEdits === 'object' && keyOrEdits !== null)
      ? keyOrEdits
      : { [keyOrEdits]: val };
    setTweaks((prev) => ({ ...prev, ...edits }));
  }, []);

  // Persist, and keep the legacy keys in sync for any code still reading them.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
      localStorage.setItem('instructorTheme', tweaks.theme);
      localStorage.setItem('instructorSidebarCollapsed', tweaks.sidebar === 'icon' ? '1' : '0');
    } catch (_) { /* storage full / disabled — non-fatal */ }
  }, [tweaks]);

  return [tweaks, setTweak];
}
